/**
 * @file React integration for the 1auth SDK.
 *
 * Exports the {@link PayButton} component — a ready-to-use payment button that
 * handles authentication, session recovery, and intent submission without
 * requiring the consuming app to manage auth state directly.
 *
 * @module @rhinestone/1auth/react
 */

import * as React from "react";
import { OneAuthClient } from "./client";
import { tokenFetchError } from "./sponsorship-fetch";
import { toOneAuthError } from "./errors";
import type {
  SendIntentOptions,
  SendIntentResult,
  CloseOnStatus,
  SponsorshipCallbackConfig,
  SponsorshipUrlConfig,
} from "./types";

// Fingerprint icon SVG
const FingerprintIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
    <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
    <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
    <path d="M2 12a10 10 0 0 1 18-6" />
    <path d="M2 16h.01" />
    <path d="M21.8 16c.2-2 .131-5.354 0-6" />
    <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
    <path d="M8.65 22c.21-.66.45-1.32.57-2" />
    <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
  </svg>
);

// Default styles
const defaultStyles: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "16px 32px",
  fontSize: "16px",
  fontWeight: 500,
  color: "#ffffff",
  backgroundColor: "#18181b",
  border: "none",
  borderRadius: "9999px",
  cursor: "pointer",
  transition: "background-color 0.2s",
  width: "100%",
};

const defaultDisabledStyles: React.CSSProperties = {
  opacity: 0.5,
  cursor: "not-allowed",
};

const defaultHoverStyles: React.CSSProperties = {
  backgroundColor: "#27272a",
};

const defaultSuccessStyles: React.CSSProperties = {
  backgroundColor: "#16a34a",
  cursor: "default",
};

// Checkmark icon SVG
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export interface PayButtonProps {
  /** The OneAuthClient instance */
  client: OneAuthClient;
  /**
   * Intent parameters (calls, targetChain, etc.). `accountAddress` is omitted
   * because PayButton resolves it internally (cached session or auth modal) and
   * overwrites it on the sendIntent call — callers pass only calls + target
   * chain, keeping the drop-in payment API intact.
   */
  intent: Omit<SendIntentOptions, "closeOn" | "accountAddress">;
  /** Called when payment succeeds */
  onSuccess?: (result: SendIntentResult) => void;
  /** Called when payment fails */
  onError?: (error: Error) => void;
  /** When to close the dialog and return success. Defaults to "preconfirmed" */
  closeOn?: CloseOnStatus;
  /** Button text - defaults to "Pay with 1auth" */
  children?: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Custom inline styles (merged with defaults) */
  style?: React.CSSProperties;
  /** Disabled state */
  disabled?: boolean;
  /** Hide the fingerprint icon */
  hideIcon?: boolean;
}

/**
 * A pre-built payment button that integrates 1auth passkey authentication
 * and intent submission into a single interactive element.
 *
 * The button manages its own loading and success states. On first click it
 * recovers the last authenticated user from `localStorage`; if none is found
 * it opens the auth modal before submitting the intent. On subsequent clicks
 * the stored session is reused so the user only needs to re-authenticate when
 * their session has been invalidated server-side.
 *
 * @param props - See {@link PayButtonProps} for the full set of options.
 *
 * @example
 * ```tsx
 * import { PayButton } from "@rhinestone/1auth/react";
 * import { client } from "./auth"; // your OneAuthClient instance
 *
 * <PayButton
 *   client={client}
 *   intent={{
 *     targetChain: 8453, // Base
 *     calls: [{ to: "0xRecipient", data: "0x", value: "1000000" }],
 *   }}
 *   onSuccess={(result) => console.log("tx:", result.transactionHash)}
 *   onError={(err) => console.error(err)}
 * >
 *   Pay $5 USDC
 * </PayButton>
 * ```
 */
export function PayButton({
  client,
  intent,
  onSuccess,
  onError,
  closeOn = "preconfirmed",
  children = "Pay with 1auth",
  className,
  style,
  disabled,
  hideIcon,
}: PayButtonProps) {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = async () => {
    if (disabled || isProcessing || isSuccess) return;

    setIsProcessing(true);

    try {
      await executePayment();
    } catch (err) {
      if (err instanceof Error && !err.message.includes("rejected")) {
        onError?.(err);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Core payment execution with built-in session recovery and re-auth retry.
   *
   * Flow:
   * 1. On first call (`forceReauth = false`), attempt to load a cached user
   *    from `localStorage` under the key `"1auth-user"`. If the JSON is
   *    malformed the cache entry is removed so the next call starts clean.
   * 2. If no cached user exists (or `forceReauth` is `true`), open the auth
   *    modal. On success, persist `{ address }` to `localStorage`
   *    so subsequent clicks skip authentication.
   * 3. Submit the intent. If the server responds with "User not found" — which
   *    happens when a cached account address no longer exists on the server —
   *    clear `localStorage` and call `executePayment(true)` to force a fresh
   *    authentication before retrying once.
   *
   * @param forceReauth - When `true`, skip the localStorage lookup and go
   *   straight to the auth modal. Used internally on "User not found" errors
   *   to recover from a stale cache without requiring a second user gesture.
   */
  const executePayment = async (forceReauth = false) => {
    // Try to get existing account address from localStorage
    let accountAddress: string | null = null;
    if (!forceReauth) {
      const savedUser = localStorage.getItem("1auth-user");
      if (savedUser) {
        try {
          const parsed = JSON.parse(savedUser);
          accountAddress = parsed.address || null;
        } catch {
          localStorage.removeItem("1auth-user");
        }
      }
    }

    // If no account address (or forced reauth), authenticate first
    if (!accountAddress) {
      const authResult = await client.authWithModal();
      if (authResult.success && authResult.user) {
        accountAddress = authResult.user.address || null;
        const stored: Record<string, string> = {};
        if (accountAddress) stored.address = accountAddress;
        if (authResult.signerType) stored.signerType = authResult.signerType;
        localStorage.setItem("1auth-user", JSON.stringify(stored));
      } else {
        // Auth cancelled or failed
        return;
      }
    }

    // Send the intent
    let result: SendIntentResult;
    try {
      result = await client.sendIntent({
        ...intent,
        // Guaranteed non-null: the `if (!accountAddress) { ...return }` guard
        // above either resolved an address or bailed out before reaching here.
        accountAddress,
        closeOn,
      });
    } catch (err) {
      // If user not found, clear localStorage and force re-authentication
      if (err instanceof Error && err.message.includes("User not found")) {
        localStorage.removeItem("1auth-user");
        return executePayment(true);
      }
      throw err;
    }

    if (result.success) {
      setIsSuccess(true);
      onSuccess?.(result);
    } else {
      // If user not found error in result, clear localStorage and retry
      if (result.error?.message?.includes("User not found")) {
        localStorage.removeItem("1auth-user");
        return executePayment(true);
      }
      onError?.(toOneAuthError(result.error, "Payment failed", "INTENT_FAILED"));
    }
  };

  const combinedStyles: React.CSSProperties = {
    ...defaultStyles,
    ...(isSuccess ? defaultSuccessStyles : {}),
    ...(isHovered && !disabled && !isProcessing && !isSuccess ? defaultHoverStyles : {}),
    ...(disabled || isProcessing ? defaultDisabledStyles : {}),
    ...style,
  };

  return (
    <button
      type="button"
      className={className}
      style={combinedStyles}
      onClick={handleClick}
      disabled={disabled || isProcessing || isSuccess}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isSuccess ? (
        <>
          <CheckIcon className="pay-button-icon" />
          Paid
        </>
      ) : isProcessing ? (
        "Processing..."
      ) : (
        <>
          {!hideIcon && <FingerprintIcon className="pay-button-icon" />}
          {children}
        </>
      )}
    </button>
  );
}

/**
 * Margin (ms) before a JWT's `exp` at which the cached access token is
 * treated as stale. Covers clock skew between the minting app server and
 * the orchestrator's verifier plus in-flight time of the prepare call —
 * a token that verifies fine locally can still be rejected downstream if
 * it expires mid-request.
 */
const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 60_000;

/**
 * Check whether a cached JWT access token is still safely usable.
 *
 * Decodes the payload WITHOUT verifying the signature — the client only
 * needs the `exp` claim to know when to re-mint; verification happens
 * server-side. Returns `false` (unusable) when the token is expired or
 * within {@link ACCESS_TOKEN_EXPIRY_MARGIN_MS} of expiry. Tokens that
 * can't be decoded or carry no numeric `exp` are treated as unusable so
 * a malformed cache entry never gets served indefinitely.
 */
export function isAccessTokenFresh(token: string): boolean {
  try {
    const payload = token.split(".")[1];
    if (!payload) return false;
    // base64url → base64 before atob; JWTs use the url-safe alphabet.
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { exp?: unknown };
    if (typeof claims.exp !== "number") return false;
    return Date.now() < claims.exp * 1000 - ACCESS_TOKEN_EXPIRY_MARGIN_MS;
  } catch {
    return false;
  }
}

/**
 * Build a {@link SponsorshipCallbackConfig} from token endpoint URLs with an
 * expiry-aware access-token cache held in a closure.
 *
 * Exported as the non-React core of {@link useSponsorship} so the caching
 * behaviour is unit-testable (and usable outside React). The access token is
 * cached and re-used only while its `exp` claim is comfortably in the
 * future — a long-lived tab must re-mint rather than serve a token the
 * orchestrator will reject with a 401 (`"exp" claim timestamp check
 * failed`), which previously surfaced to users as "Failed to get quote from
 * orchestrator". The extension token is always fetched fresh per intent
 * because it binds to a specific `intentOp`.
 *
 * Both fetches use `credentials: "include"` so the app's own session cookies
 * authenticate the user — no separate auth channel needed.
 */
export function createCachedSponsorship(
  config: SponsorshipUrlConfig,
): SponsorshipCallbackConfig {
  const { accessTokenUrl, extensionTokenUrl } = config;
  let cachedToken: string | null = null;

  const fetchAccessToken = async (): Promise<string> => {
    const res = await fetch(accessTokenUrl, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) {
      // Folds the server's JSON error reason into the message so
      // misconfigurations aren't reduced to an opaque HTTP status.
      throw await tokenFetchError("sponsorship access token", res);
    }
    const body = (await res.json()) as { token?: string };
    if (!body.token) throw new Error("Missing `token` in access-token response");
    cachedToken = body.token;
    return body.token;
  };

  return {
    accessToken: async () =>
      cachedToken && isAccessTokenFresh(cachedToken)
        ? cachedToken
        : await fetchAccessToken(),
    getExtensionToken: async (intentOp: string) => {
      const res = await fetch(extensionTokenUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intentOp }),
      });
      if (res.status === 401) {
        // Access token likely expired — drop cached copy so the next
        // accessToken() call re-mints, then surface the error for the
        // integrator to retry.
        cachedToken = null;
      }
      if (!res.ok) {
        throw await tokenFetchError("sponsorship extension token", res);
      }
      const body = (await res.json()) as { token?: string };
      if (!body.token) throw new Error("Missing `token` in extension-token response");
      return body.token;
    },
  };
}

/**
 * React hook wrapper around {@link createCachedSponsorship}. The returned
 * config is stable across renders (memoized by URL), so passing it to
 * `new OneAuthClient({ sponsorship })` or `client.setSponsorship(...)` does
 * not thrash the client. Re-creating on URL change naturally resets the
 * cached token so we never serve a token minted against the previous
 * endpoint.
 */
export function useSponsorship(
  config: SponsorshipUrlConfig,
): SponsorshipCallbackConfig {
  const { accessTokenUrl, extensionTokenUrl } = config;

  return React.useMemo<SponsorshipCallbackConfig>(
    () => createCachedSponsorship({ accessTokenUrl, extensionTokenUrl }),
    [accessTokenUrl, extensionTokenUrl],
  );
}

// Export the constructor from the same entrypoint that creates PayButton
// errors. Separate tsup entry bundles do not share JavaScript class identity,
// so consumers must use this export for reliable `instanceof` checks.
export { OneAuthError } from "./errors";

// Re-export types for convenience
export type { SendIntentOptions, SendIntentResult, CloseOnStatus } from "./types";
